/** XeAgents.ai**/
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { subDays } from "date-fns";
import { formatDate } from "@/lib/date-format";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/Pagination";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Search, Loader2, User, Calendar, Filter, RefreshCw,
  AlertTriangle, AlertCircle, Info, XCircle,
  Phone, CreditCard, Shield, Webhook, Activity,
  ChevronDown, ChevronRight, Clock, ArrowUpDown
} from "lucide-react";

interface DiagnosticEvent {
  id: string;
  type: string;
  category: string;
  timestamp: string;
  severity: string;
  title: string;
  description: string;
  metadata: Record<string, any>;
}

interface UserInfo {
  id: string;
  email: string;
  name: string;
  planType: string;
  credits: number;
  isActive: boolean;
  createdAt: string;
}

interface DiagnosticsResponse {
  user: UserInfo;
  events: DiagnosticEvent[];
  summary: {
    total: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
  };
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
  filters: {
    startDate: string;
    endDate: string;
    eventTypes: string[];
  };
}

interface SearchUser {
  id: string;
  email: string;
  name: string;
  planType: string;
  isActive: boolean;
  createdAt: string;
}

const EVENT_TYPE_OPTIONS = [
  { value: 'audit', label: 'Audit Logs', icon: Shield },
  { value: 'call', label: 'Calls', icon: Phone },
  { value: 'credit', label: 'Credit Transactions', icon: CreditCard },
  { value: 'credit_deduction', label: 'Credit Deductions', icon: Activity },
  { value: 'webhook', label: 'Webhooks', icon: Webhook },
];

const DATE_RANGE_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

export default function UserDiagnostics() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState("30");
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(['audit', 'call', 'credit', 'credit_deduction', 'webhook']);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const pageSize = 25;

  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    const end = new Date();
    const days = parseInt(dateRange, 10);
    const start = subDays(end, days);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString()
    };
  }, [dateRange]);

  // Search users
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['/api/admin/user-diagnostics-search', searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return { users: [] };
      const response = await apiRequest("GET", `/api/admin/user-diagnostics-search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      if (!response.ok) throw new Error('Failed to search users');
      return response.json();
    },
    enabled: searchQuery.length >= 2,
    staleTime: 30000,
  });

  // Fetch diagnostics for selected user
  const { data: diagnostics, isLoading: diagnosticsLoading, refetch } = useQuery<DiagnosticsResponse>({
    queryKey: ['/api/admin/user-diagnostics', selectedUserId, startDate, endDate, selectedEventTypes.join(','), page],
    queryFn: async () => {
      if (!selectedUserId) return null;
      const params = new URLSearchParams({
        startDate,
        endDate,
        eventTypes: selectedEventTypes.join(','),
        page: page.toString(),
        limit: pageSize.toString()
      });
      const response = await apiRequest("GET", `/api/admin/user-diagnostics/${selectedUserId}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch diagnostics');
      return response.json();
    },
    enabled: !!selectedUserId,
    staleTime: 0,
  });

  const toggleEventType = (type: string) => {
    setSelectedEventTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
    setPage(1);
  };

  const toggleExpanded = (eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Info className="h-4 w-4 text-blue-500" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      error: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
      warning: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
      info: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
    };
    return variants[severity] || variants.info;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="h-4 w-4" />;
      case 'credit':
      case 'credit_deduction':
        return <CreditCard className="h-4 w-4" />;
      case 'webhook':
        return <Webhook className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      audit: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      call: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      credit: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      credit_deduction: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      webhook: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400'
    };
    return colors[type] || colors.audit;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">User Diagnostics</h2>
        <p className="text-muted-foreground">
          View timeline of events and troubleshoot issues for any user
        </p>
      </div>

      {/* User Search */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" />
            Select User
          </CardTitle>
          <CardDescription>
            Search by email or name to view their activity timeline
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {searchLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Search Results Dropdown */}
          {searchResults?.users && searchResults.users.length > 0 && (
            <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-auto">
              {searchResults.users.map((user: SearchUser) => (
                <button
                  key={user.id}
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setSearchQuery("");
                    setPage(1);
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors flex items-center justify-between ${
                    selectedUserId === user.id ? 'bg-muted' : ''
                  }`}
                >
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{user.planType}</Badge>
                    {!user.isActive && (
                      <Badge variant="destructive">Inactive</Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Selected User Info */}
          {diagnostics?.user && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">{diagnostics.user.name}</p>
                  <p className="text-sm text-muted-foreground">{diagnostics.user.email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Credits</p>
                    <p className="font-semibold">{diagnostics.user.credits.toLocaleString()}</p>
                  </div>
                  <Badge variant={diagnostics.user.isActive ? "default" : "destructive"}>
                    {diagnostics.user.isActive ? "Active" : "Inactive"}
                  </Badge>
                  <Badge variant="outline">{diagnostics.user.planType}</Badge>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      {selectedUserId && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date Range */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date Range
                </Label>
                <Select value={dateRange} onValueChange={(v) => { setDateRange(v); setPage(1); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Event Types */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Event Types
                </Label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_TYPE_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                        selectedEventTypes.includes(option.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <Checkbox
                        checked={selectedEventTypes.includes(option.value)}
                        onCheckedChange={() => toggleEventType(option.value)}
                        className="sr-only"
                      />
                      <option.icon className="h-3.5 w-3.5" />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={showErrorsOnly}
                  onCheckedChange={(checked) => { setShowErrorsOnly(checked as boolean); setPage(1); }}
                />
                <span className="text-sm font-medium flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Show Errors Only
                </span>
              </label>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {diagnostics?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{diagnostics.summary.total}</div>
              <p className="text-sm text-muted-foreground">Total Events</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">
                {(diagnostics.summary.bySeverity.error || 0) + (diagnostics.summary.bySeverity.critical || 0)}
              </div>
              <p className="text-sm text-muted-foreground">Errors</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{diagnostics.summary.bySeverity.warning || 0}</div>
              <p className="text-sm text-muted-foreground">Warnings</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{diagnostics.summary.byType.call || 0}</div>
              <p className="text-sm text-muted-foreground">Calls</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{diagnostics.summary.byType.credit || 0}</div>
              <p className="text-sm text-muted-foreground">Transactions</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timeline */}
      {diagnosticsLoading ? (
        <Card>
          <CardContent className="py-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : diagnostics?.events && diagnostics.events.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Event Timeline
              {showErrorsOnly && (
                <Badge variant="destructive" className="ml-2">Errors Only</Badge>
              )}
            </CardTitle>
            <CardDescription>
              {showErrorsOnly
                ? `Showing ${diagnostics.events.filter(e => e.severity === 'error' || e.severity === 'critical').length} errors of ${diagnostics.pagination.totalItems} total events`
                : `Showing ${diagnostics.events.length} of ${diagnostics.pagination.totalItems} events`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border" />

              <div className="space-y-4">
                {diagnostics.events
                  .filter(event => !showErrorsOnly || event.severity === 'error' || event.severity === 'critical')
                  .map((event) => (
                  <Collapsible
                    key={event.id}
                    open={expandedEvents.has(event.id)}
                    onOpenChange={() => toggleExpanded(event.id)}
                  >
                    <div className="relative pl-12">
                      {/* Timeline dot */}
                      <div className="absolute left-0 top-3 flex items-center justify-center w-10 h-10 rounded-full bg-background border-2 border-border">
                        {getTypeIcon(event.type)}
                      </div>

                      <div className="border rounded-lg p-4 hover:bg-muted/30 transition-colors">
                        <CollapsibleTrigger className="w-full text-left">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {getSeverityIcon(event.severity)}
                                <span className="font-medium">{event.title}</span>
                                <Badge className={getTypeBadge(event.type)} variant="secondary">
                                  {event.type.replace('_', ' ')}
                                </Badge>
                                <Badge className={getSeverityBadge(event.severity)} variant="secondary">
                                  {event.severity}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 truncate">
                                {event.description}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
                              <span>{formatDate(event.timestamp, "MMM d, HH:mm:ss")}</span>
                              {expandedEvents.has(event.id) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </div>
                          </div>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <div className="mt-4 pt-4 border-t">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              {Object.entries(event.metadata)
                                .filter(([_, v]) => v !== null && v !== undefined && v !== '')
                                .map(([key, value]) => (
                                  <div key={key}>
                                    <p className="text-muted-foreground capitalize">
                                      {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </p>
                                    <p className="font-mono text-xs break-all">
                                      {typeof value === 'object'
                                        ? JSON.stringify(value)
                                        : String(value)}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </div>
                    </div>
                  </Collapsible>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {diagnostics.pagination.totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={diagnostics.pagination.page}
                  totalPages={diagnostics.pagination.totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </CardContent>
        </Card>
      ) : selectedUserId ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No events found</h3>
            <p className="text-muted-foreground mt-1">
              No events match your current filters. Try adjusting the date range or event types.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Select a user to view diagnostics</h3>
            <p className="text-muted-foreground mt-1">
              Search for a user by email or name above to view their activity timeline
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
